import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import {
  catchError, map, timeout, finalize,
} from 'rxjs/operators';
import { environment } from '../../environment/environment';
import { V2AddressComponents, V2GisResponse } from './republic.service.type';

export interface AddressComponents {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  stateCode: string;
  postalCode: string;
  country: string;
  display: string;
}

export interface GISResponse {
  polygon: string;
  division: string;
  lawson: string;
  success: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GisService {
  // Loading state for UI feedback
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  // V2 GIS lookup with new request/response format
  getV2GISDataFromAddress(addressComponents: V2AddressComponents): Observable<V2GisResponse> {
    this.loadingSubject.next(true);

    const requestBody = {
      address_line_1: addressComponents.address_line_1,
      address_line_2: addressComponents.address_line_2 || '',
      city: addressComponents.city,
      state: addressComponents.state,
      zip_code: addressComponents.zip_code,
    };

    console.log('V2 GIS Request Body:', requestBody);

    // Start with minimal headers to avoid CORS issues
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': environment.v2_gis_api_key,
    });

    // Add Authorization if it exists
    const authToken = localStorage.getItem('Authorization');
    if (authToken) {
      headers = headers.set('Authorization', authToken);
    }

    const url = `${environment.v2_endpoint}/gis/gis-lookup`;
    console.log('V2 GIS Request URL:', url);
    console.log('V2 GIS Request Headers:', {
      'Content-Type': 'application/json',
      'x-api-key': '[REDACTED]',
      Authorization: localStorage.getItem('Authorization') ? 'Bearer [REDACTED]' : 'None',
    });

    return this.http.post<any>(url, requestBody, { headers })
      .pipe(
        timeout(10000),
        map((response) => {
          console.log('V2 GIS Raw Response:', response);
          console.log('V2 GIS Response Type:', typeof response);
          console.log('V2 GIS Response Keys:', Object.keys(response));
          return this.processV2GISResponse(response);
        }),
        catchError((error) => {
          console.error('V2 GIS HTTP Error caught in pipe:', error);
          return this.handleV2GISError(error);
        }),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  // Convert address to GIS data (polygon, division, lawson)
  getGISDataFromAddress(addressComponents: AddressComponents): Observable<GISResponse> {
    this.loadingSubject.next(true);

    const requestBody = {
      addressLine1: addressComponents.addressLine1,
      addressLine2: addressComponents.addressLine2 || '',
      city: addressComponents.city,
      stateCode: addressComponents.stateCode,
      postalCode: addressComponents.postalCode,
    };

    console.log('GIS Request Body:', requestBody);

    const headers = new HttpHeaders({
      Authorization: localStorage.getItem('Authorization') || '',
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(`${environment.end_point}/resolve-address`, requestBody, { headers })
      .pipe(
        timeout(10000),
        map((response) => {
          console.log('GIS Raw Response:', response);
          return this.processGISResponse(response);
        }),
        catchError((error) => this.handleGISError(error)),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  // Process and validate API response
  private processGISResponse(response: any): GISResponse {
    try {
      console.log('Processing GIS Response:', response);

      if (response.status === 'success' && response.polygon !== undefined) {
        const gisResult: GISResponse = {
          polygon: response.polygon || '',
          lawson: response.lawson || '',
          division: response.division || '',
          success: true,
        };

        console.log('GIS Result:', gisResult);
        return gisResult;
      }
      console.warn('GIS Response Error:', response);
      return {
        polygon: '',
        lawson: '',
        division: '',
        success: false,
        error: response.error || response.message || 'GIS resolution failed',
      };
    } catch (error) {
      console.error('Error processing GIS response:', error);
      return {
        polygon: '',
        lawson: '',
        division: '',
        success: false,
        error: 'Failed to process GIS response',
      };
    }
  }

  // Process and validate V2 API response
  private processV2GISResponse(response: any): V2GisResponse {
    try {
      console.log('Processing V2 GIS Response:', response);

      // Handle different possible response formats
      let actualResponse = response;

      // Check if response is wrapped in a body property
      if (response && response.body) {
        try {
          actualResponse = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
          console.log('Parsed body response:', actualResponse);
        } catch (e) {
          console.warn('Could not parse body as JSON:', response.body);
        }
      }

      // Check for the expected v2 format (fields can be empty strings)
      if (actualResponse && actualResponse.polygon !== undefined
          && actualResponse.lawson !== undefined
          && actualResponse.division !== undefined) {
        const gisResult: V2GisResponse = {
          polygon: actualResponse.polygon || '',
          lawson: actualResponse.lawson || '',
          division: actualResponse.division || '',
        };

        console.log('V2 GIS Result:', gisResult);
        return gisResult;
      }

      console.warn('V2 GIS Response Error - Missing required fields:', actualResponse);
      console.warn('Expected: polygon, lawson, division');
      console.warn('Available keys:', Object.keys(actualResponse || {}));
      const errorMsg = 'Invalid V2 GIS response format. Missing required fields: polygon, lawson, division';
      throw new Error(errorMsg);
    } catch (error) {
      console.error('Error processing V2 GIS response:', error);
      throw error;
    }
  }

  // Handle V2 GIS errors with error field in body
  private handleV2GISError(error: any): Observable<V2GisResponse> {
    let errorMessage = 'GIS service error';

    console.error('V2 GIS Service Error:', error);

    // Try to extract error message from various response formats
    try {
      console.error('V2 GIS Error Status:', error.status);
      console.error('V2 GIS Error Body:', error.error);
      console.error('V2 GIS Error Message:', error.message);
      console.error('V2 GIS Full Error Object:', JSON.stringify(error, null, 2));
      // Format 1: Direct error object with error field
      if (error.error?.error) {
        errorMessage = error.error.error;
      } else if (error.error?.body) {
        // Format 2: Error body as string that needs parsing
        try {
          const errorBody = JSON.parse(error.error.body);
          errorMessage = errorBody.error || 'No service area found';
        } catch (parseError) {
          errorMessage = 'No service area found';
        }
      } else if (error.error?.message) {
        // Format 3: Error response with message field
        errorMessage = error.error.message;
      } else if (typeof error.error === 'string') {
        // Format 4: Direct string error in error field
        try {
          const parsedError = JSON.parse(error.error);
          errorMessage = parsedError.error || parsedError.message || error.error;
        } catch {
          errorMessage = error.error;
        }
      } else if (error.message) {
        // Format 5: Error message in main error object
        errorMessage = error.message;
      } else if (error.status === 404) {
        // Handle specific HTTP status codes
        errorMessage = 'No service areas found';
      } else if (error.name === 'TimeoutError') {
        errorMessage = 'GIS service timeout - please try again';
      } else if (error.status === 401) {
        errorMessage = 'GIS service authentication failed - invalid or missing auth token';
      } else if (error.status === 403) {
        errorMessage = 'GIS service access forbidden - check API key, auth token, or Lambda permissions';
      } else if (error.status >= 500) {
        errorMessage = 'GIS service temporarily unavailable';
      } else if (error.status === 0) {
        errorMessage = 'Network error - unable to connect to GIS service';
      } else {
        errorMessage = `GIS service error (${error.status || 'unknown'})`;
      }
    } catch (parseError) {
      console.error('Error parsing error response:', parseError);
      errorMessage = 'Unable to process error response';
    }

    console.error('Final V2 GIS Error Message:', errorMessage);

    // For v2, we throw the error to be handled by the calling component
    // since v2 doesn't have a success/error flag in the response
    throw new Error(errorMessage);
  }

  // Handle different types of API errors with specific messages
  private handleGISError(error: any): Observable<GISResponse> {
    let errorMessage = 'GIS service error';

    if (error.name === 'TimeoutError') {
      errorMessage = 'GIS service timeout - please try again';
    } else if (error.status === 401) {
      errorMessage = 'GIS service authentication failed';
    } else if (error.status === 404) {
      errorMessage = 'Service is not provided at this address';
    } else if (error.status >= 500) {
      errorMessage = 'GIS service temporarily unavailable';
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    console.error('GIS Service Error:', error);

    return of({
      polygon: '',
      division: '',
      lawson: '',
      success: false,
      error: errorMessage,
    });
  }
}
